import React, { useMemo, useState } from 'react';
import { Button, Input, Modal, Select, Space, Table, Typography, message } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import SoftTag from '../components/SoftTag';
import { brandSemantic } from '../config/brand';

const { Search } = Input;
const { Title } = Typography;
const { confirm } = Modal;

export default function UsersManagementPage({
  users,
  currentUser,
  setUsers,
  handleUserStatusChange,
  showModal,
  userForm,
  canDelete = true,
}) {
  const [userSearch, setUserSearch] = useState('');

  const roleTones = {
    admin: { color: brandSemantic.info, background: brandSemantic.infoSoft, border: brandSemantic.infoBorder },
    'sales manager': { color: brandSemantic.success, background: brandSemantic.successSoft, border: brandSemantic.successBorder },
    'sales user': { color: brandSemantic.success, background: brandSemantic.successSoft, border: brandSemantic.successBorder },
    'procurement manager': { color: brandSemantic.warning, background: brandSemantic.warningSoft, border: brandSemantic.warningBorder },
    'procurement user': { color: brandSemantic.warning, background: brandSemantic.warningSoft, border: brandSemantic.warningBorder },
  };
  const statusTones = {
    Active: { color: brandSemantic.success, background: brandSemantic.successSoft, border: brandSemantic.successBorder },
    Inactive: { color: brandSemantic.warning, background: brandSemantic.warningSoft, border: brandSemantic.warningBorder },
  };
  const getUserStatusSelectStyle = (active) => ({
    width: 92,
    '--status-color': active ? statusTones.Active.color : statusTones.Inactive.color,
    '--status-bg': active ? statusTones.Active.background : statusTones.Inactive.background,
    '--status-border': active ? statusTones.Active.border : statusTones.Inactive.border,
  });

  const filteredUsers = useMemo(() => {
    const normalizedSearch = userSearch.trim().toLowerCase();
    if (!normalizedSearch) return users;
    return users.filter((user) => (
      user.name.toLowerCase().includes(normalizedSearch)
      || user.email.toLowerCase().includes(normalizedSearch)
      || user.login.toLowerCase().includes(normalizedSearch)
    ));
  }, [userSearch, users]);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Login',
      dataIndex: 'login',
      key: 'login',
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role) => (
        <SoftTag tone={roleTones[role]}>
          {role}
        </SoftTag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      render: (active, record) => (
        <Select
          value={active ? 'Active' : 'Inactive'}
          onChange={(value) => handleUserStatusChange(record.id, value === 'Active')}
          size="small"
          variant="borderless"
          className="status-tag-select"
          style={getUserStatusSelectStyle(active)}
          dropdownMatchSelectWidth={false}
          options={[
            { value: 'Active', label: 'Active' },
            { value: 'Inactive', label: 'Inactive' },
          ]}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space direction="vertical" size={4}>
          <Button
            type="link"
            icon={<EditOutlined />}
            style={{ padding: 0, height: 'auto', color: brandSemantic.primary }}
            onClick={() => {
              showModal('editUser', record);
            }}
          >
            Edit
          </Button>
          {canDelete && record.id !== currentUser?.id && (
            <Button
              type="link"
              icon={<DeleteOutlined />}
              style={{ padding: 0, height: 'auto', color: brandSemantic.error }}
              onClick={() => {
                confirm({
                  title: 'Delete User',
                  content: 'Are you sure you want to delete this user?',
                  onOk() {
                    setUsers(users.filter((user) => user.id !== record.id));
                    message.success('User deleted');
                  },
                });
              }}
            >
              Delete
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="page-toolbar" style={{ marginBottom: '16px' }}>
        <Title level={2} className="page-toolbar-title" style={{ margin: 0 }}>User Management</Title>
        <div className="page-toolbar-controls">
          <Search
            placeholder="Search by name, email or login"
            allowClear
            size="large"
            style={{ width: 320 }}
            value={userSearch}
            onChange={(event) => setUserSearch(event.target.value)}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={() => {
              userForm.resetFields();
              showModal('createUser');
            }}
          >
            Add User
          </Button>
        </div>
      </div>
      <Table
        columns={columns}
        dataSource={filteredUsers}
        rowKey="id"
        rowClassName={(record) => (record.active ? '' : 'inactive-table-row')}
        scroll={{ x: 'max-content' }}
        pagination={{ pageSize: 10, position: ['bottomCenter'] }}
        bordered
        size="middle"
      />
    </div>
  );
}
